import React, { useEffect, useMemo, useState } from 'react';

import { useAccount, useUser } from '~/apis';
import { uiLogger } from '~/app';
import { selectGameType, useSelector } from '~/app/selectors';
import { useProfileNavigation } from '~/profile/profileNavigation';
import { GameType } from '~/server/src/dtos';
import {
  generateBotAvatarDataUrl,
  getInitials,
  getProfilePictureUrl,
  isLocalBot,
} from '~/utils/profilePicture';
import { cn } from '~/utils/styles';

interface ChatAvatarProps {
  userId: string | undefined;
  username: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const ChatAvatar: React.FC<ChatAvatarProps> = ({ userId, username, size = 'sm', className }) => {
  const gameType = useSelector(selectGameType);
  const isLocalMode = gameType === GameType.Training;
  const [botAvatarUrl, setBotAvatarUrl] = useState<string | undefined>(undefined);
  const [imageFailed, setImageFailed] = useState(false);
  const { data: account } = useAccount();
  const { openUserProfile } = useProfileNavigation();

  // Detect local bot: in local mode and userId starts with 'bot_'
  const isLocalBotUser = isLocalBot(userId, isLocalMode);

  // Don't fetch user data for bot players or in local mode
  const shouldFetchUser = !isLocalMode && userId && userId !== '';

  const { data: user } = useUser(userId || '', {
    enabled: !!shouldFetchUser,
  });

  // Generate bot avatar only for local bots; when !isLocalBotUser we use getProfilePictureUrl in display
  useEffect(() => {
    if (isLocalBotUser && userId) {
      generateBotAvatarDataUrl(userId)
        .then(setBotAvatarUrl)
        .catch((error) => {
          uiLogger.warn({ err: error }, 'Failed to generate bot avatar');
          setBotAvatarUrl(undefined);
        });
    }
  }, [isLocalBotUser, userId]);

  // If local bot, use generated avatar, otherwise use profile picture URL
  const profilePictureUrl = isLocalBotUser
    ? botAvatarUrl
    : getProfilePictureUrl(user?.profilePicture);
  const initials = getInitials(username);

  const sizeClassName = useMemo(() => {
    switch (size) {
      case 'xs':
        return 'h-3.5 w-3.5 text-[9px]';
      case 'sm':
        return 'h-5 w-5 text-[10px]'; // 20px (normal mode target)
      case 'md':
        return 'h-6 w-6 text-[11px]';
      case 'lg':
        return 'h-8 w-8 text-xs';
      case 'xl':
        return 'h-10 w-10 text-sm';
      default:
        return 'h-5 w-5 text-[10px]';
    }
  }, [size]);

  const baseClassName = cn(sizeClassName, 'rounded-full', className);

  const handleAvatarClick = () => {
    if (userId && !isLocalBotUser) {
      openUserProfile(userId, account?.userId);
    }
  };

  // Don't make bot avatars clickable
  const isClickable = userId && !isLocalBotUser;

  if (!profilePictureUrl || imageFailed) {
    if (isClickable) {
      return (
        <button
          className={cn(
            baseClassName,
            'flex cursor-pointer items-center justify-center bg-gray-400 font-semibold text-white transition-opacity hover:opacity-80',
          )}
          onClick={handleAvatarClick}
          aria-label={`View ${username}'s profile`}
        >
          {initials}
        </button>
      );
    }

    return (
      <div
        className={cn(
          baseClassName,
          'flex items-center justify-center bg-gray-400 font-semibold text-white',
        )}
      >
        {initials}
      </div>
    );
  }

  if (isClickable) {
    return (
      <button
        className={cn(baseClassName, 'cursor-pointer transition-opacity hover:opacity-80')}
        onClick={handleAvatarClick}
        aria-label={`View ${username}'s profile`}
      >
        <img
          src={profilePictureUrl}
          alt={username}
          className="h-full w-full rounded-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            setImageFailed(true);
          }}
        />
      </button>
    );
  }

  return (
    <img
      src={profilePictureUrl}
      alt={username}
      className={cn(baseClassName, 'object-cover')}
      onError={() => setImageFailed(true)}
    />
  );
};

export default ChatAvatar;
